import {
  Box,
  ButtonClean,
  ButtonDanger,
  ButtonOutline,
  ButtonPrimary,
  Modal,
  Text
} from '../../../../components'
import { brandColors } from 'shared/basicPalette'
import { t, Trans } from '@lingui/macro'
import React, { FC, useEffect } from 'react'
import {
  useSetMsCalendarIntegrationMutation,
  useRenewMsCalendarIntegrationMutation
} from '../AuthenticationTab.codegen'
import { CalendarIntegrationsProps } from './CalendarIntegrations'
import { useModal } from '../../../../app/hooks/useModal'
import { notification } from '../../../../components/common/notifications/Notifications'
import Label from '../../../../components/common/form/label/Label'
import Input from '../../../../components/common/form/input/Input'
import { FormRow } from '../../../../components/common/form/Form'
import { SyncAlert } from 'front-end/src/pages/manage-events/components/sync-alert/SyncAlert'

let MSAuthCallback:
  | ((
      event: Event & { data: { payload: { authorizationCode: string } } }
    ) => Promise<void>)
  | null = null
let MSAuthPopup: Window | null = null

export const MSGraphIntegration: FC<
  Pick<
    CalendarIntegrationsProps,
    'calendars' | 'calendarsRefetch' | 'msAuthUrl'
  >
> = ({ calendars, calendarsRefetch, msAuthUrl }) => {
  const [setMSAuth] = useSetMsCalendarIntegrationMutation({
    onCompleted: () => {
      calendarsRefetch()
      notification.info(t`Calendar integrations updated successfully.`)
    }
  })
  const [renewMSAuthResolver] = useRenewMsCalendarIntegrationMutation({
    onCompleted: () => {
      calendarsRefetch()
      notification.info(t`Calendar integrations updated successfully.`)
    }
  })

  const showRevokeModal = () => {
    open(DisableMSCalendarSyncModal).then(async () => {
      await saveMSAuth()
    })
  }
  const { open, currentModal } = useModal()

  useEffect(() => {
    return () => {
      if (MSAuthCallback) window.removeEventListener('message', MSAuthCallback)
      if (MSAuthPopup) MSAuthPopup.close()
    }
  })

  const saveMSAuth = async (authCode = '') => {
    await setMSAuth({
      variables: {
        authCode
      }
    })
  }

  const renewMSAuth = async (authCode: string) => {
    await renewMSAuthResolver({
      variables: {
        authCode
      }
    })
  }

  const openMSAuthPopup = (renew = false) => {
    if (MSAuthPopup && !MSAuthPopup?.closed) {
      MSAuthPopup?.focus()
    } else if (msAuthUrl) {
      if (MSAuthCallback) window.removeEventListener('message', MSAuthCallback)
      if (MSAuthPopup) MSAuthPopup = null

      MSAuthPopup = window.open(msAuthUrl, '_blank', 'width=700, height=700')

      MSAuthCallback = async (event) => {
        const authCode = event.data.payload.authorizationCode
        if (authCode) {
          if (renew) {
            await renewMSAuth(authCode)
          } else {
            await saveMSAuth(authCode)
          }
          MSAuthPopup?.close()
        }
      }
      window.addEventListener('message', MSAuthCallback, false)
    }
  }

  return (
    <>
      {calendars?.msgraph?.accountName ? (
        <>
          <FormRow className="AccountPage__calendarInput">
            <Label>
              <Trans>Autenticated user</Trans>
            </Label>
            <Input value={calendars?.msgraph?.accountName} disabled />
          </FormRow>
          <Text color={brandColors.grey.mid}>
            <Trans>Microsoft Outlook integration is now active</Trans>
          </Text>
          <ButtonOutline mt="12px" mr="12px" onClick={() => showRevokeModal()}>
            <Trans>Revoke</Trans>
          </ButtonOutline>
          <ButtonOutline
            mt="12px"
            mr="12px"
            onClick={() => openMSAuthPopup(true)}
          >
            <Trans>Renew</Trans>
          </ButtonOutline>

          <Box mt="12px" mb="12px">
            <SyncAlert hasDismiss={false} />
          </Box>
        </>
      ) : (
        <>
          <Text fontSize="sm" color={brandColors.grey.mid}>
            <Trans>
              It is recommended to authenticate with a designated service
              account
            </Trans>
          </Text>

          <ButtonPrimary mt="12px" onClick={() => openMSAuthPopup()}>
            <Trans>Authenticate</Trans>
          </ButtonPrimary>
        </>
      )}

      {currentModal}
    </>
  )
}

const DisableMSCalendarSyncModal = ({ resolve, reject }) => (
  <Modal
    newDesign
    header={t`Are you sure you want to disable MS Calendar sync?`}
    controls={
      <>
        <ButtonClean onClick={reject}>
          <Trans>Cancel</Trans>
        </ButtonClean>
        <ButtonDanger onClick={resolve}>
          <Trans>Disable</Trans>
        </ButtonDanger>
      </>
    }
    onHide={reject}
  >
    <p>
      <Trans>MS calendar synchronization will be disabled.</Trans>
    </p>
  </Modal>
)
